/**
 * theme.js — GlowUp Nutrition
 * Zawiera: accordion, cart drawer, sticky CTA, exit popup, announcement rotation
 */

/* ==========================================================================
   AKORDEON
   ========================================================================== */

class NutriAccordion extends HTMLElement {
  connectedCallback() {
    this.querySelectorAll('.accordion-trigger').forEach((trigger) => {
      trigger.addEventListener('click', () => this.toggle(trigger));
    });
  }

  toggle(trigger) {
    const expanded = trigger.getAttribute('aria-expanded') === 'true';
    const contentId = trigger.getAttribute('aria-controls');
    const content = document.getElementById(contentId);

    // Zamknij inne (opcjonalne — tylko jeśli atrybut data-solo)
    if (this.dataset.solo !== undefined) {
      this.querySelectorAll('.accordion-trigger').forEach((t) => {
        if (t !== trigger) {
          t.setAttribute('aria-expanded', 'false');
          const id = t.getAttribute('aria-controls');
          const c = document.getElementById(id);
          if (c) c.setAttribute('aria-hidden', 'true');
        }
      });
    }

    trigger.setAttribute('aria-expanded', String(!expanded));
    if (content) content.setAttribute('aria-hidden', String(expanded));
  }
}

customElements.define('nutri-accordion', NutriAccordion);

/* ==========================================================================
   CART DRAWER
   ========================================================================== */

class CartDrawer extends HTMLElement {
  connectedCallback() {
    this.overlay = document.querySelector('.cart-drawer__overlay');
    this.freeShippingThreshold = parseInt(this.dataset.freeShipping || '20000', 10);
    this.debounceTimers = {};
    // Every cart mutation bumps the version; responses requested under an older version are
    // stale and must not be rendered (they would resurrect a line that is being removed).
    this.version = 0;
    this.removingKeys = new Set(); // data-item-id of lines being removed

    const closeBtn = this.querySelector('.cart-drawer__close');
    if (closeBtn) closeBtn.addEventListener('click', () => this.close());

    // Events
    document.addEventListener('cart:open', () => this.open());
    document.addEventListener('cart:updated', (e) => {
      if (e.detail?.source !== 'cart-drawer') {
        this.fetchCart(false);
      }
    });

    // Delegation for +/- quantity, remove links, upsell buttons, close button
    this.addEventListener('click', async (e) => {
      const closeBtn = e.target.closest('.cart-drawer__close');
      if (closeBtn) {
        e.preventDefault();
        this.close();
        return;
      }

      const decBtn = e.target.closest('[data-action="decrement"]');
      const incBtn = e.target.closest('[data-action="increment"]');
      const removeBtn = e.target.closest('.cart-item__remove');
      const upsellAddBtn = e.target.closest('.cart-drawer__upsell-add-btn');

      if (removeBtn) {
        e.preventDefault();
        const cartItem = removeBtn.closest('.cart-item');
        const line = removeBtn.getAttribute('data-line') || cartItem?.getAttribute('data-line');
        const key = removeBtn.getAttribute('data-key') || cartItem?.getAttribute('data-key');
        if (line || key) {
          this.removeItem(cartItem, line, key);
        }
        return;
      }

      if (decBtn || incBtn) {
        e.preventDefault();
        const btn = decBtn || incBtn;
        const selector = btn.closest('.quantity-selector');
        const input = selector?.querySelector('.quantity-selector__input');
        const cartItem = btn.closest('.cart-item');
        if (!input) return;

        const line = input.getAttribute('data-line') || cartItem?.getAttribute('data-line');
        const key = input.getAttribute('data-key') || cartItem?.getAttribute('data-key');
        if (!line && !key) return;

        let currentVal = parseInt(input.value, 10);
        if (isNaN(currentVal)) currentVal = 1;

        const newVal = decBtn ? currentVal - 1 : currentVal + 1;

        if (newVal <= 0) {
          this.removeItem(cartItem, line, key);
        } else {
          input.value = newVal;
          if (cartItem) cartItem.classList.add('is-updating');
          this.scheduleQuantityUpdate(line, newVal, key);
        }
        return;
      }

      if (upsellAddBtn) {
        e.preventDefault();
        const variantId = upsellAddBtn.getAttribute('data-variant-id');
        if (!variantId) return;

        await window.NutriCart.add({items: [{id: Number(variantId), quantity: 1}]}, upsellAddBtn);
      }
    });

    this.addEventListener('change', (e) => {
      const offer = e.target.closest('[data-ebook-offer]');
      if (offer) return this.toggleEbookOffer(offer);
      const input = e.target.closest('.quantity-selector__input');
      if (input) {
        const line = input.getAttribute('data-line');
        const key = input.getAttribute('data-key');
        const val = parseInt(input.value, 10);
        if (!isNaN(val)) input.value = val;
        if (!isNaN(val) && (line || key)) {
          if (val <= 0) {
            const cartItem = input.closest('.cart-item');
            this.removeItem(cartItem, line, key);
          } else {
            this.scheduleQuantityUpdate(line, val, key);
          }
        }
      }
    });

    // Close on ESC
    document.addEventListener('keydown', (e) => {
      if (!this.classList.contains('is-open')) return;
      if (e.key === 'Escape') this.close();
      if (e.key !== 'Tab') return;
      const controls = [...this.querySelectorAll('button:not([disabled]),a[href],input:not([disabled])')].filter(control => control.getClientRects().length);
      const first = controls[0], last = controls.at(-1);
      if (!this.contains(document.activeElement) || (!e.shiftKey && document.activeElement === last)) {e.preventDefault();first?.focus();}
      else if (e.shiftKey && document.activeElement === first) {e.preventDefault();last?.focus();}
    });

    // Close on overlay click
    if (this.overlay) {
      this.overlay.addEventListener('click', () => this.close());
    }
    this.enforceCartRules();
  }

  // Cart rules rendered as [data-cart-fix] markers: e-book 39 zł only with Na Serce (snippets/ebook-cart.liquid),
  // free shaker from the threshold (snippets/shaker-gift.liquid). One fix per render; the re-render shows the next.
  // A fix that fails is not retried until reload, so a broken product can't cause a request loop.
  async enforceCartRules() {
    const fix = this.querySelector('[data-cart-fix]');
    // On /cart the page script applies the fix (and reloads); doing it here too would add the gift twice.
    if (!fix || this.cartFixing || document.querySelector('.cart-page')) return;
    const id = fix.dataset.add || `${fix.dataset.key}:${fix.dataset.qty}`;
    this.cartFixFailed = this.cartFixFailed || new Set();
    if (this.cartFixFailed.has(id)) return;
    this.cartFixing = true;
    try {
      if (fix.dataset.add) {
        this.version++;
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {'Content-Type': 'application/json', 'Accept': 'application/json'},
          body: JSON.stringify({items: [{id: Number(fix.dataset.add), quantity: 1}], sections: 'cart-drawer'})
        });
        if (!res.ok) throw new Error('Gift add failed');
        const data = await res.json();
        this.cartFixing = false;
        this.renderDrawerHtml(data.sections?.['cart-drawer']);
        this.updateHeaderCountFromCart();
      } else {
        const res = await fetch('/cart/change.js', {
          method: 'POST',
          headers: {'Content-Type': 'application/json', 'Accept': 'application/json'},
          body: JSON.stringify({id: fix.dataset.key, quantity: Number(fix.dataset.qty), sections: 'cart-drawer'})
        });
        if (!res.ok) throw new Error('Cart fix failed');
        const data = await res.json();
        this.version++;
        this.cartFixing = false;
        this.renderDrawerHtml(data.sections?.['cart-drawer']);
        this.updateCount(data.item_count);
      }
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { source: 'cart-drawer' } }));
    } catch (err) {
      console.error('Cart rule error:', err);
      this.cartFixFailed.add(id);
    } finally {
      this.cartFixing = false;
    }
  }

  async toggleEbookOffer(input) {
    input.closest('.ebook-offer')?.classList.add('is-busy');
    this.version++;
    try {
      const res = await fetch('/cart/update.js', {
        method: 'POST',
        headers: {'Content-Type': 'application/json', 'Accept': 'application/json'},
        body: JSON.stringify({updates: ebookOfferUpdates(input), sections: 'cart-drawer'})
      });
      if (!res.ok) throw new Error('Ebook offer update failed');
      const data = await res.json();
      this.renderDrawerHtml(data.sections?.['cart-drawer']);
      this.updateCount(data.item_count);
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: data, source: 'cart-drawer' } }));
    } catch (err) {
      console.error('Ebook offer error:', err);
      await this.fetchCart(true);
    }
  }

  async removeItem(cartItem, line, key) {
    this.version++;
    const itemId = cartItem?.dataset.itemId;
    if (itemId) this.removingKeys.add(itemId);
    if (key) clearTimeout(this.debounceTimers[key]);
    if (cartItem) this.collapse(cartItem);

    try {
      const payload = {
        quantity: 0,
        sections: 'cart-drawer'
      };
      if (key) {
        payload.id = key;
      } else {
        payload.line = parseInt(line, 10);
      }

      // Let the collapse finish before the drawer re-renders, however fast the response is.
      const [res] = await Promise.all([fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(payload)
      }), new Promise((resolve) => setTimeout(resolve, 280))]);

      if (!res.ok) throw new Error('Remove item failed');

      const data = await res.json();
      this.renderDrawerHtml(data.sections?.['cart-drawer']);
      this.updateCount(data.item_count);
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: data, source: 'cart-drawer' } }));
    } catch (err) {
      console.error('Remove item error:', err);
      if (cartItem) { cartItem.classList.remove('is-removing'); cartItem.style.maxHeight = ''; }
      await this.fetchCart(true);
    } finally {
      if (itemId) this.removingKeys.delete(itemId);
    }
  }

  // Collapse from the line's real height (a fixed max-height makes tall lines jump first).
  collapse(item) {
    item.style.maxHeight = `${item.scrollHeight}px`;
    void item.offsetHeight;
    item.classList.add('is-removing');
    item.style.maxHeight = '0px';
  }

  scheduleQuantityUpdate(line, quantity, key) {
    const timerKey = key || line;
    if (this.debounceTimers[timerKey]) {
      clearTimeout(this.debounceTimers[timerKey]);
    }
    this.debounceTimers[timerKey] = setTimeout(() => {
      this.updateQuantity(line, quantity, key);
    }, 200);
  }

  // `html` (the cart-drawer section from add.js) renders instantly; without it the drawer fetches itself.
  async open(html) {
    if (html) {
      this.version++;
      this.renderDrawerHtml(html);
      this.updateHeaderCountFromCart();
    }
    if (!this.classList.contains('is-open')) this.returnFocus = document.activeElement;
    this.setAttribute('aria-hidden','false');
    this.overlay = this.overlay || document.querySelector('.cart-drawer__overlay');
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    if (scrollbarWidth > 0) {
      document.body.style.paddingRight = `${scrollbarWidth}px`;
    }
    this.classList.add('is-open');
    if (this.overlay) {
      this.overlay.classList.add('is-open');
      this.overlay.setAttribute('aria-hidden', 'false');
    }
    document.body.classList.add('cart-drawer-open');
    document.documentElement.style.overflow = 'hidden';

    if (!html) await this.fetchCart(false);
    this.querySelector('.cart-drawer__close')?.focus();
  }

  close() {
    this.setAttribute('aria-hidden','true');
    this.returnFocus?.focus?.();
    this.overlay = this.overlay || document.querySelector('.cart-drawer__overlay');
    this.classList.remove('is-open');
    if (this.overlay) {
      this.overlay.classList.remove('is-open');
      this.overlay.setAttribute('aria-hidden', 'true');
    }
    document.body.classList.remove('cart-drawer-open');
    document.documentElement.style.overflow = '';
    document.body.style.paddingRight = '';
  }

  renderDrawerHtml(html) {
    if (!html) return this.fetchCart(true);
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const newDrawer = doc.querySelector('cart-drawer');
    // Line keys change when Shopify re-prices the cart, so lines are matched by variant (+ gift flag).
    const idsIn = (root) => new Set([...root.querySelectorAll('.cart-item[data-item-id]')].map((el) => el.dataset.itemId));
    doc.querySelectorAll('.cart-item[data-item-id]').forEach((el) => { if (this.removingKeys.has(el.dataset.itemId)) el.remove(); });
    const before = idsIn(this), after = idsIn(doc);
    doc.querySelectorAll('.cart-item[data-item-id]').forEach((el) => { if (before.size && !before.has(el.dataset.itemId)) el.classList.add('is-entering'); });
    const vanishing = [...this.querySelectorAll('.cart-item[data-item-id]:not(.is-removing)')].filter((el) => !after.has(el.dataset.itemId));
    if (vanishing.length) {
      vanishing.forEach((el) => this.collapse(el));
      const version = this.version;
      setTimeout(() => { if (version === this.version) this.swapDrawer(doc, newDrawer); }, 280);
      return;
    }
    this.swapDrawer(doc, newDrawer);
  }

  swapDrawer(doc, newDrawer) {

    if (newDrawer) {
      this.innerHTML = newDrawer.innerHTML;
      if (newDrawer.dataset.freeShipping) {
        this.dataset.freeShipping = newDrawer.dataset.freeShipping;
      }
    } else {
      const sectionContent = doc.querySelector('.shopify-section') || doc.body;
      if (sectionContent) {
        const innerDrawer = sectionContent.querySelector('cart-drawer');
        this.innerHTML = innerDrawer ? innerDrawer.innerHTML : sectionContent.innerHTML;
      }
    }
    const closeBtn = this.querySelector('.cart-drawer__close');
    if (closeBtn) closeBtn.addEventListener('click', () => this.close());
    this.enforceCartRules();
  }

  async updateHeaderCountFromCart() {
    try {
      const res = await fetch('/cart.js');
      if (res.ok) {
        const cart = await res.json();
        this.updateCount(cart.item_count);
      }
    } catch (err) {
      // ignore
    }
  }

  async fetchCart(broadcast = true, sync = true) {
    const version = this.version;
    try {
      const res = await fetch('/?section_id=cart-drawer');
      if (!res.ok) throw new Error('Section fetch error');

      const html = await res.text();
      if (version !== this.version) return; // a newer change will render its own result
      this.renderDrawerHtml(html);
      await this.updateHeaderCountFromCart();

      if (broadcast) {
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: { source: 'cart-drawer' } }));
      }
    } catch (err) {
      console.error('Cart fetch error:', err);
    }
  }

  async updateQuantity(line, quantity, key) {
    this.version++;
    try {
      const payload = {
        quantity: parseInt(quantity, 10),
        sections: 'cart-drawer'
      };
      if (key) {
        payload.id = key;
      } else {
        payload.line = parseInt(line, 10);
      }

      const res = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (!res.ok) throw new Error('Cart update failed');

      const data = await res.json();
      if (data.sections && data.sections['cart-drawer']) {
        this.renderDrawerHtml(data.sections['cart-drawer']);
      } else {
        await this.fetchCart(true);
      }

      if (data.item_count !== undefined) {
        this.updateCount(data.item_count);
      } else {
        await this.updateHeaderCountFromCart();
      }

      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: data, source: 'cart-drawer' } }));
    } catch (err) {
      console.error('Update quantity error:', err);
      this.fetchCart(true);
    }
  }

  updateCount(count) {
    document.querySelectorAll('[data-cart-count]').forEach((el) => {
      el.textContent = count;
      el.style.display = count > 0 ? '' : 'none';
    });
  }
}

customElements.define('cart-drawer', CartDrawer);

// Checking the 39 zł offer adds it (and swaps out a 99 zł copy already in the cart); unchecking removes it.
function ebookOfferUpdates(input) {
  const updates = {[input.dataset.ebookOffer]: input.checked ? 1 : 0};
  if (input.checked && input.dataset.ebookReplace) updates[input.dataset.ebookReplace] = 0;
  return updates;
}

// The /cart page applies the same cart rules, then reloads (only after a successful change, so no reload loop).
document.addEventListener('DOMContentLoaded', () => {
  const page = document.querySelector('.cart-page');
  if (!page) return;
  const post = (url, body) => fetch(url, {
    method: 'POST',
    headers: {'Content-Type': 'application/json', 'Accept': 'application/json'},
    body: JSON.stringify(body)
  }).then((res) => { if (res.ok) location.reload(); });
  const fix = page.querySelector('[data-cart-fix]');
  if (fix) post(fix.dataset.add ? '/cart/add.js' : '/cart/change.js', fix.dataset.add ? {items: [{id: Number(fix.dataset.add), quantity: 1}]} : {id: fix.dataset.key, quantity: Number(fix.dataset.qty)});
  page.addEventListener('change', (e) => {
    const offer = e.target.closest('[data-ebook-offer]');
    if (!offer) return;
    offer.closest('.ebook-offer')?.classList.add('is-busy');
    post('/cart/update.js', {updates: ebookOfferUpdates(offer)});
  });
});

/* ==========================================================================
   ADD TO CART — przechwycenie formularza
   ========================================================================== */

class AddToCartForm extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    if (!this.form) return;

    this.form.addEventListener('submit', async (e) => {
      e.preventDefault();
      e.stopPropagation();

      const btn = this.form.querySelector('[type="submit"], [name="add"], .product-form__atc');
      await window.NutriCart.add(new FormData(this.form), btn);
    });
  }
}

customElements.define('add-to-cart-form', AddToCartForm);

/* ==========================================================================
   STICKY ADD TO CART (karta produktu)
   ========================================================================== */

class StickyAtc extends HTMLElement {
  connectedCallback() {
    const productHero = document.querySelector('.product-hero-form');
    if (!productHero) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        this.classList.toggle('is-visible', !entry.isIntersecting);
      },
      { threshold: 0 }
    );
    observer.observe(productHero);

    const btn = this.querySelector('.sticky-atc__btn');
    if (btn) {
      btn.addEventListener('click', () => {
        const mainForm = document.querySelector('.product-hero-form form');
        if (mainForm) mainForm.dispatchEvent(new Event('submit', { cancelable: true }));
      });
    }
  }
}

customElements.define('sticky-atc', StickyAtc);

/* ==========================================================================
   EXIT INTENT POPUP
   ========================================================================== */

(function initExitPopup() {
  const popup = document.querySelector('.exit-popup');
  if (!popup) return;

  // Pokaż tylko raz na sesję
  if (sessionStorage.getItem('exitPopupShown')) return;

  let popupTriggered = false;

  const show = () => {
    if (popupTriggered) return;
    popupTriggered = true;
    sessionStorage.setItem('exitPopupShown', '1');
    popup.classList.add('is-open');
  };

  const close = () => popup.classList.remove('is-open');

  // Trigger na desktop — ruch myszy poza górną krawędź
  document.addEventListener('mouseleave', (e) => {
    if (e.clientY <= 5) show();
  });

  // Zamknij przyciskiem
  popup.querySelector('.exit-popup__close')?.addEventListener('click', close);
  popup.querySelector('.exit-popup__overlay')?.addEventListener('click', close);

  // Kopiuj kod
  const codeEl = popup.querySelector('.exit-popup__code');
  if (codeEl) {
    codeEl.addEventListener('click', () => {
      navigator.clipboard?.writeText(codeEl.textContent.trim());
      codeEl.dataset.copied = 'true';
      setTimeout(() => delete codeEl.dataset.copied, 2000);
    });
  }

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') close();
  });
})();

/* ==========================================================================
   HEADER — MOBILE MENU TOGGLE
   ========================================================================== */

class MobileMenu extends HTMLElement {
  connectedCallback() {
    this.toggle = this.querySelector('[data-mobile-toggle]');
    this.drawer = document.querySelector('[data-mobile-drawer]');

    if (this.toggle && this.drawer) {
      this.toggle.addEventListener('click', (e) => {
        e.preventDefault();
        const isOpen = this.drawer.classList.contains('is-open');
        this.drawer.classList.toggle('is-open', !isOpen);
        this.toggle.setAttribute('aria-expanded', String(!isOpen));
        document.body.style.overflow = !isOpen ? 'hidden' : '';
      });
    }
  }
}

customElements.define('mobile-menu', MobileMenu);

/* ==========================================================================
   CART ICON — otwiera drawer zamiast przejścia na /cart
   ========================================================================== */

document.addEventListener('click', (e) => {
  const cartBtn = e.target.closest('[data-cart-trigger]');
  if (cartBtn) {
    e.preventDefault();
    document.dispatchEvent(new Event('cart:open'));
  }
});

/* ==========================================================================
   ANNOUNCEMENT BAR — auto-rotation (jeśli tryb static)
   ========================================================================== */

(function initAnnouncementRotation() {
  const bar = document.querySelector('.announcement-bar--rotating');
  if (!bar) return;
  const items = bar.querySelectorAll('.announcement-bar__rotate-item');
  if (items.length <= 1) return;

  let current = 0;

  const show = (index) => {
    items.forEach((item, i) => {
      item.style.display = i === index ? 'flex' : 'none';
    });
  };

  show(0);
  setInterval(() => {
    current = (current + 1) % items.length;
    show(current);
  }, 4000);
})();

/* ==========================================================================
   QUANTITY SELECTOR (GLOBAL PDP & FORMS)
   ========================================================================== */

document.addEventListener('click', (e) => {
  const dec = e.target.closest('.quantity-selector [data-action="decrement"]');
  const inc = e.target.closest('.quantity-selector [data-action="increment"]');

  if (dec || inc) {
    const selector = (dec || inc).closest('.quantity-selector');
    if (!selector) return;

    // CartDrawer handles its own clicks via internal delegation
    if (selector.closest('cart-drawer')) return;

    e.preventDefault();
    const input = selector.querySelector('.quantity-selector__input');
    if (!input) return;

    const min = parseInt(input.getAttribute('min') || '1', 10);
    const max = parseInt(input.getAttribute('max') || '99', 10);
    const val = parseInt(input.value || '1', 10);

    if (dec) {
      if (val > min) input.value = val - 1;
    } else if (inc) {
      if (val < max) input.value = val + 1;
    }

    input.dispatchEvent(new Event('change', { bubbles: true }));
  }
});
/* ==========================================================================
   PREDICTIVE SEARCH
   ========================================================================== */

class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('[data-search-input]');
    this.results = this.querySelector('[data-search-results]');
    this.triggers = document.querySelectorAll('[data-search-trigger]');
    this.closeButtons = this.querySelectorAll('[data-search-close]');
    this.emptyState = this.querySelector('[data-search-empty]');

    this.setupEventListeners();
  }

  setupEventListeners() {
    document.addEventListener('click', (e) => {
      const trigger = e.target.closest('[data-search-trigger]');
      const closeBtn = e.target.closest('[data-search-close]');

      if (trigger) {
        e.preventDefault();
        e.stopPropagation();
        this.open();
      } else if (closeBtn && this.classList.contains('is-open')) {
        e.preventDefault();
        this.close();
      }
    });

    if (this.input) {
      this.input.addEventListener('input', this.debounce((event) => {
        this.onChange(event);
      }, 250).bind(this));
    }

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.classList.contains('is-open')) this.close();
    });
  }

  open() {
    const mobileDrawer = document.querySelector('[data-mobile-drawer]');
    if (mobileDrawer && mobileDrawer.classList.contains('is-open')) {
      mobileDrawer.classList.remove('is-open');
    }

    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    if (scrollbarWidth > 0) {
      document.body.style.paddingRight = `${scrollbarWidth}px`;
    }

    this.classList.add('is-open');
    document.documentElement.style.overflow = 'hidden';

    setTimeout(() => {
      if (this.input) {
        this.input.value = '';
        this.input.focus();
      }
      if (this.results) this.results.innerHTML = '';
      if (this.emptyState) this.emptyState.classList.add('hide');
    }, 100);
  }

  close() {
    this.classList.remove('is-open');
    document.documentElement.style.overflow = '';
    document.body.style.paddingRight = '';
  }

  onChange() {
    const searchTerm = this.input.value.trim();

    if (!searchTerm.length) {
      this.results.innerHTML = '';
      this.emptyState.classList.add('hide');
      return;
    }

    this.getSearchResults(searchTerm);
  }

  async getSearchResults(searchTerm) {
    try {
      const response = await fetch(`/search/suggest.json?q=${encodeURIComponent(searchTerm)}&resources[type]=product&resources[limit]=4&section_id=search-drawer`);
      
      // Shopify's Predictive Search API can return JSON or HTML if section_id is provided
      // For simplicity and better control, we will use JSON and render locally
      const searchRes = await fetch(`/search/suggest.json?q=${encodeURIComponent(searchTerm)}&resources[type]=product&resources[limit]=4`);
      const data = await searchRes.json();
      
      const products = data.resources.results.products;
      this.renderResults(products);

    } catch (error) {
      console.error('Predictive Search Error:', error);
    }
  }

  renderResults(products) {
    if (!products || products.length === 0) {
      this.results.innerHTML = '';
      this.emptyState.classList.remove('hide');
      return;
    }

    this.emptyState.classList.add('hide');
    
    let html = '<ul class="predictive-search__list" role="listbox">';
    
    products.forEach(product => {
      html += `
        <li class="predictive-search__item" role="option">
          <a href="${product.url}" class="predictive-search__link">
            <div class="predictive-search__image">
              <img src="${product.image || ''}" alt="${product.title}" loading="lazy">
            </div>
            <div class="predictive-search__info">
              <span class="predictive-search__title">${product.title}</span>
              <span class="predictive-search__price">${product.price_min_as_string || ''}</span>
            </div>
          </a>
        </li>
      `;
    });

    html += '</ul>';
    
    if (products.length > 0) {
      html += `
        <div class="search-drawer__view-all">
          <a href="/search?q=${encodeURIComponent(this.input.value.trim())}*" class="btn btn--ghost">ZOBACZ WSZYSTKIE WYNIKI</a>
        </div>
      `;
    }

    this.results.innerHTML = html;
  }

  debounce(fn, wait) {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), wait);
    };
  }
}

customElements.define('predictive-search', PredictiveSearch);

/* ==========================================================================
   PRODUCT SLIDER (STAGE 2 IMPROVEMENT)
   ========================================================================== */

class ProductSlider extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('.product-slider__track');
    this.prevBtn = this.querySelector('.product-slider__arrow--prev');
    this.nextBtn = this.querySelector('.product-slider__arrow--next');
    this.sortBtn = this.querySelector('.product-slider__sort-btn');
    this.sortLabel = this.querySelector('.product-slider__sort-label');

    if (!this.track) return;

    this.prevBtn?.addEventListener('click', () => this.scroll('left'));
    this.nextBtn?.addEventListener('click', () => this.scroll('right'));
    this.sortBtn?.addEventListener('click', () => this.toggleSort());
    if (this.sortBtn) this.sortProducts(this.sortBtn.dataset.sortDirection || 'desc');

    // Update button states on scroll
    this.track.addEventListener('scroll', () => this.toggleButtons(), { passive: true });
    this.toggleButtons();

    // Re-check button state when layout/images update
    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(() => this.toggleButtons());
      this.resizeObserver.observe(this.track);
    }
    this.onResize = () => this.toggleButtons();
    window.addEventListener('resize', this.onResize);

    // Also update buttons after images inside load
    this.track.querySelectorAll('img').forEach((img) => {
      if (!img.complete) {
        img.addEventListener('load', () => this.toggleButtons(), { once: true });
      }
    });

    this.initMouseDrag();
  }

  disconnectedCallback() {
    if (this.onResize) window.removeEventListener('resize', this.onResize);
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
  }

  initMouseDrag() {
    // Native touch devices use hardware-accelerated CSS scrolling (Safari iOS / Android)
    if (window.matchMedia('(pointer: coarse)').matches) return;

    let isDown = false;
    let startX = 0;
    let scrollLeft = 0;
    let hasDragged = false;

    this.track.addEventListener('mousedown', (e) => {
      // Ignore click on interactive buttons inside product cards (like quick ATC)
      if (e.target.closest('button, a, input, select')) return;
      isDown = true;
      hasDragged = false;
      this.track.style.cursor = 'grabbing';
      this.track.style.userSelect = 'none';
      startX = e.pageX - this.track.offsetLeft;
      scrollLeft = this.track.scrollLeft;
    });

    const stopDragging = () => {
      if (!isDown) return;
      isDown = false;
      this.track.style.cursor = '';
      this.track.style.removeProperty('user-select');
    };

    this.track.addEventListener('mouseleave', stopDragging);
    this.track.addEventListener('mouseup', stopDragging);

    this.track.addEventListener('mousemove', (e) => {
      if (!isDown) return;
      const x = e.pageX - this.track.offsetLeft;
      const walk = (x - startX) * 1.5;
      if (Math.abs(walk) > 5) {
        hasDragged = true;
        e.preventDefault();
        this.track.scrollLeft = scrollLeft - walk;
      }
    });

    // Prevent navigation click on card links if user was dragging
    this.track.addEventListener('click', (e) => {
      if (hasDragged) {
        e.preventDefault();
        e.stopPropagation();
        hasDragged = false;
      }
    }, true);
  }

  scroll(direction) {
    const items = this.track.querySelectorAll('.product-slider__item');
    if (items.length === 0) return;

    let scrollStep = 0;
    if (items.length > 1) {
      scrollStep = items[1].offsetLeft - items[0].offsetLeft;
    } else {
      scrollStep = items[0].offsetWidth + 20;
    }

    const scrollAmount = direction === 'left' ? -scrollStep : scrollStep;
    this.track.scrollBy({ left: scrollAmount, behavior: 'smooth' });
  }

  toggleButtons() {
    if (!this.prevBtn || !this.nextBtn) return;
    const scrollLeft = this.track.scrollLeft;
    const maxScroll = this.track.scrollWidth - this.track.clientWidth;

    // If all items fit without scrollable overflow
    if (maxScroll <= 5) {
      this.prevBtn.setAttribute('disabled', 'true');
      this.nextBtn.setAttribute('disabled', 'true');
      return;
    }

    // Prev button state
    if (scrollLeft <= 5) {
      this.prevBtn.setAttribute('disabled', 'true');
    } else {
      this.prevBtn.removeAttribute('disabled');
    }

    // Next button state
    if (scrollLeft >= maxScroll - 5) {
      this.nextBtn.setAttribute('disabled', 'true');
    } else {
      this.nextBtn.removeAttribute('disabled');
    }
  }

  toggleSort() {
    if (!this.sortBtn) return;
    const currentDir = this.sortBtn.getAttribute('data-sort-direction') || 'desc';
    const newDir = currentDir === 'desc' ? 'asc' : 'desc';

    this.sortBtn.setAttribute('data-sort-direction', newDir);
    if (this.sortLabel) {
      this.sortLabel.textContent = newDir === 'desc' ? 'Cena: Od najwyższej' : 'Cena: Od najniższej';
    }

    this.sortProducts(newDir);
  }

  sortProducts(direction) {
    const items = Array.from(this.track.querySelectorAll('.product-slider__item'));
    if (items.length === 0) return;

    items.sort((a, b) => {
      const priceA = parseInt(a.getAttribute('data-price') || '0');
      const priceB = parseInt(b.getAttribute('data-price') || '0');
      return direction === 'desc' ? priceB - priceA : priceA - priceB;
    });

    // Re-append items in sorted order
    items.forEach(item => this.track.appendChild(item));

    // Reset scroll to start after sorting
    this.track.scrollTo({ left: 0, behavior: 'smooth' });
    this.toggleButtons();
  }
}

customElements.define('product-slider', ProductSlider);

/* ==========================================================================
   PRODUCT GALLERY (STAGE 3 IMPROVEMENT)
   ========================================================================== */

class ProductGallery extends HTMLElement {
  connectedCallback() {
    this.mainImg = this.querySelector('.product-media__main-img');
    this.thumbnails = this.querySelectorAll('.product-media__thumbnail');

    this.thumbnails.forEach(thumb => {
      thumb.addEventListener('click', () => this.switchImage(thumb));
    });
  }

  switchImage(selectedThumb) {
    if (!this.mainImg) return;
    
    const newUrl = selectedThumb.getAttribute('data-image-url');
    if (!newUrl) return;

    // Fade out main image
    this.mainImg.style.opacity = '0';

    setTimeout(() => {
      // Clear srcset first to ensure the browser repaints using only the src attribute
      if (this.mainImg.hasAttribute('srcset')) {
        this.mainImg.removeAttribute('srcset');
      }
      this.mainImg.setAttribute('src', newUrl);
      
      // Update active thumbnail class
      this.thumbnails.forEach(thumb => thumb.classList.remove('is-active'));
      selectedThumb.classList.add('is-active');

      // Fade in main image
      this.mainImg.style.opacity = '1';
    }, 200);
  }
}

customElements.define('product-gallery', ProductGallery);

// Dodanie gwiazdki przed wysłaniem formularza wyszukiwania
document.addEventListener('submit', (e) => {
  const form = e.target;
  if (form && form.getAttribute('role') === 'search') {
    const input = form.querySelector('input[name="q"]');
    if (input && input.value.trim().length > 0) {
      const value = input.value.trim();
      if (!value.endsWith('*')) {
        input.value = value + '*';
      }
    }
  }
});


